import { useEffect } from 'react';

/**
 * Landing page for siply.pro, the guest-facing domain.
 *
 * Until now the bare root fell through to the staff dashboard route and therefore to the
 * SpiritVault login: anyone who typed the domain or half-scanned a QR code met a business
 * login form. This answers the guest first, in one screen, and only then talks to venue
 * owners further down.
 *
 * Deliberately built in the visual language of the menus themselves (same dark ground,
 * same sand accent, same narrow phone-width column), so arriving here feels like the
 * hallway of the card you are about to open.
 */
export default function SiplyLanding() {
  useEffect(() => {
    const previous = document.title;
    document.title = 'Siply – die Karte deines Lokals';
    return () => {
      document.title = previous;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-[#f1f5f9] antialiased">
      {/* ---------- For guests ---------- */}
      <section className="mx-auto flex min-h-[88vh] max-w-2xl flex-col items-center justify-center px-6 py-16 text-center">
        <p className="font-display text-xl tracking-[0.14em] text-[#d4a574]">Siply</p>

        {/* The one bold object: the card that actually stands on the table */}
        <div className="relative mt-14 mb-12 [perspective:900px]">
          <div
            aria-hidden="true"
            className="absolute -bottom-6 left-1/2 h-10 w-44 -translate-x-1/2 rounded-[50%] bg-[#d4a574]/10 blur-xl"
          />
          <div className="siply-tent relative w-[170px] rounded-xl border border-[#d4a574]/40 bg-gradient-to-b from-[#1b2740] to-[#0f1828] px-6 py-8 shadow-[0_22px_45px_-20px_rgba(0,0,0,0.95)]">
            <span className="block text-sm text-[#8a9ab0]">Tisch</span>
            <span className="mt-1 block font-display text-6xl leading-none text-[#d4a574]">6</span>
          </div>
        </div>

        <h1 className="max-w-[18ch] text-4xl font-semibold leading-[1.15] tracking-[-0.02em] text-white sm:text-5xl">
          Die Karte liegt schon auf dem Tisch.
        </h1>

        <p className="mt-5 max-w-[42ch] text-lg leading-relaxed text-[#8a9ab0]">
          Scanne den Code auf deinem Tisch, und die Karte deines Lokals öffnet sich. Ohne App,
          ohne Anmeldung.
        </p>

        <ul className="mt-10 space-y-3 text-left text-[15px] text-[#cbd5e1]">
          {[
            'Speisen und Getränke ansehen, mit Fotos und Allergenen',
            'Direkt vom Tisch bestellen',
            'Kellner rufen oder zahlen, ohne zu winken',
          ].map((line) => (
            <li key={line} className="flex items-baseline gap-3">
              <span aria-hidden="true" className="h-px w-5 shrink-0 translate-y-[-4px] bg-[#d4a574]" />
              {line}
            </li>
          ))}
        </ul>
      </section>

      {/* ---------- For venue owners ----------
          The column widens and the ground lifts: a different audience, a different room.
          No divider rule needed, the change of measure does the work. */}
      <section className="border-t border-white/5 bg-[#0d1524]">
        <div className="mx-auto max-w-4xl px-6 py-20">
          <h2 className="font-display text-3xl text-white sm:text-4xl">Für Gastronomen</h2>

          <p className="mt-5 max-w-[62ch] text-lg leading-relaxed text-[#8a9ab0]">
            Siply ist die Gästekarte von SpiritVault Pro. Ihre Gäste scannen den Code am Tisch,
            die Bestellung landet ohne Umweg in der Küche, und Sie ändern Preise und Artikel
            selbst – sichtbar auf allen Tischen im selben Moment.
          </p>

          <div className="mt-12 grid gap-x-12 gap-y-8 sm:grid-cols-2">
            {[
              {
                title: 'Karte selbst pflegen',
                body: 'Artikel, Preise und Fotos ändern, ohne auf jemanden zu warten. Ausverkauftes mit einem Tipp ausblenden.',
              },
              {
                title: 'Allergene und Diät-Angaben',
                body: 'EU-14-Kennzeichnung pro Artikel, dazu vegan, vegetarisch, glutenfrei. Gäste filtern selbst.',
              },
              {
                title: 'Bestellung an den Tisch',
                body: 'Was der Gast bestellt, steht sofort auf dem Küchen-Display – getrennt nach Bar und Küche.',
              },
              {
                title: 'Rufen und zahlen am Tisch',
                body: 'Gäste melden sich selbst, wenn sie etwas brauchen oder zahlen möchten. Ihr Personal sieht es auf dem Handy.',
              },
              {
                title: 'Tischplan und laufende Rechnung',
                body: 'Ein Tab pro Tisch, Rechnungen teilen, Tische zusammenlegen.',
              },
              {
                title: 'Happy Hour und Empfehlungen',
                body: 'Zeitfenster-Preise laufen automatisch, und zu jedem Artikel lässt sich Passendes vorschlagen.',
              },
            ].map((feature) => (
              <div key={feature.title}>
                <h3 className="text-base font-semibold text-white">{feature.title}</h3>
                <p className="mt-1.5 text-[15px] leading-relaxed text-[#8a9ab0]">{feature.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-4">
            <a
              href="https://spiritvault.pro/register"
              className="rounded-lg bg-[#d4a574] px-6 py-3 font-semibold text-[#0a0f1a] transition-colors hover:bg-[#e3bb8d] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#d4a574]"
            >
              Konto anlegen
            </a>
            <a
              href="https://spiritvault.pro"
              className="text-[15px] text-[#d4a574] underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#d4a574]"
            >
              Alle Funktionen ansehen
            </a>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center gap-x-6 gap-y-3 px-6 py-8 text-sm text-[#5d6b80]">
          <span>
            Siply ist die Gästekarte von{' '}
            <a
              href="https://spiritvault.pro"
              className="text-[#8a9ab0] underline-offset-4 hover:text-[#d4a574] hover:underline"
            >
              SpiritVault Pro
            </a>
            .
          </span>
          <span className="flex flex-wrap gap-x-5 gap-y-2">
            <a href="/impressum" className="hover:text-[#d4a574]">
              Impressum
            </a>
            <a href="/datenschutz" className="hover:text-[#d4a574]">
              Datenschutz
            </a>
            <a href="/kontakt" className="hover:text-[#d4a574]">
              Kontakt
            </a>
          </span>
        </div>
      </footer>
    </div>
  );
}
